/**
 * The optimizer result → the suggestion cards the activity feed renders.
 *
 * Each platform's optimizer emits its recommendations under its own key, and the
 * UI only ever looked in two of them:
 *
 *   analysis.actionable_suggestions   Google Ads (Search / PMax / Demand Gen)
 *   actionable_items                  MadHive / Reddit / Facebook
 *   pacing_recommendations            DV360 — the PRIMARY list (pacing-first, by
 *                                     design: see campaign_optimizer/reasoning.py)
 *   recommendations                   DV360 — secondary targeting/creative/bidding
 *
 * A DV360 run therefore rendered "No optimization suggestions available." while
 * its result JSON sat right underneath it, full of recommendations.
 *
 * DV360 items are marked `advisory`: their action vocabulary
 * (ADJUST_DAILY_BUDGET / ADD_FREQUENCY_CAP / ADJUST_FREQUENCY_CAP / ADD_AUDIENCE)
 * is not what buildSuggestionEditRequest builds edits from, so an "Apply Changes"
 * button would send prose to an edit agent that cannot act on it. Better to show
 * the recommendation and say where to apply it.
 */

/** DV360 pacing recommendation → a suggestion card. */
function fromPacingRecommendation(rec) {
  // The focused pacing launcher targets a LIST of line items
  // (line_item_ids/line_item_names); the combined optimizer a single one.
  const singleLineItemId = rec.line_item_id
    || (Array.isArray(rec.line_item_ids) && rec.line_item_ids.length === 1 ? rec.line_item_ids[0] : null);
  const firstLineItemName = rec.line_item_name
    || (Array.isArray(rec.line_item_names) ? rec.line_item_names.filter(Boolean).join(', ') || null : null);
  return {
    ...rec,
    // The card's heading falls back through plain_summary → description → title.
    title: rec.title
      || firstLineItemName
      || (singleLineItemId ? `Line item ${singleLineItemId}` : 'Pacing recommendation'),
    category: rec.category || 'Pacing',
    // Pacing status is the finding; keep it visible as the priority chip.
    priority: rec.priority || null,
    // Target the line item so the card's "target" line names it.
    target_type: rec.target_type || (singleLineItemId ? 'line_item' : null),
    target_id: rec.target_id || singleLineItemId || null,
    // The focused pacing launcher VALIDATES each recommendation into a DV360
    // field-edit payload and stamps auto_applicable — those get a real Apply
    // button. Anything unstamped (combined-optimizer output, older results)
    // keeps the historical advisory-only behavior.
    advisory: rec.auto_applicable !== true,
  };
}

/** DV360 secondary recommendation → a suggestion card. */
function fromRecommendation(rec) {
  return {
    ...rec,
    // The focused performance launcher names its action `lever`
    // (BID/KEYWORD_EXPANSION/…); surface it through the same action_type slot
    // the card already renders.
    action_type: rec.action_type || rec.lever || null,
    category: rec.category || 'Optimization',
    // The focused performance launcher validates each recommendation into a DV360
    // field-edit payload and stamps auto_applicable, exactly as the pacing one does
    // (dv360_optimizer_apply.attach_executable_operations with PERFORMANCE_SHAPE).
    // This was hard-coded `true`, so a bid change or domain expansion that the backend
    // had already built an operation for still rendered as "apply this manually" and
    // its Apply button never appeared. Anything unstamped — the combined optimizer,
    // older stored results — keeps the historical advisory-only behavior.
    advisory: rec.auto_applicable !== true,
  };
}

/**
 * A diagnostic run's blockers → actionable rows.
 *
 * The diagnostic emits no `recommendations` list, so the feed showed "No changes
 * recommended" under a run whose whole point was to say what to change. Its blockers ARE
 * the work: each one names the insertion order, the line items it covers and the single
 * fix that clears it (dv360_diagnostic_optimizer_launcher.py's blocking_issues).
 *
 * They are advisory by construction rather than because validation refused them — no
 * field edit assigns a creative or moves a flight date — so each carries the reason,
 * which the action cell renders in place of an Apply button.
 *
 * Exported rather than folded into `resolveOptimizationSuggestions` because the findings
 * renderers on other platforms already render their own blocker cards; turning these into
 * suggestions everywhere would print the same blocker twice on one page.
 */
export function blockingIssueRows(details) {
  return asArray(details?.blocking_issues)
    .filter((issue) => issue && typeof issue === 'object')
    .map((issue, i) => ({
      ...issue,
      recommendation_id: issue.issue_id || `blocker_${i + 1}`,
      title: issue.issue || issue.fix || 'Delivery blocker',
      action_type: issue.blocker || 'CLEAR_BLOCKER',
      category: 'Delivery blocker',
      // The columns a table row needs: what is true now, and what to do about it.
      current_value: issue.current_state || null,
      recommended_value: issue.fix || null,
      rationale: issue.issue || null,
      advisory: true,
      auto_applicable: false,
      advisory_reason: issue.fixed_in
        || 'A delivery blocker is cleared in the platform (creatives, flight dates, budget '
           + 'segments), not by a field edit.',
    }));
}

const asArray = (v) => (Array.isArray(v) ? v : []);

/**
 * Every suggestion in a result, in the order the optimizer meant them to be read
 * (DV360 leads with pacing). Returns [] when there are none — the caller keeps
 * showing its empty state.
 *
 * @param {object|null} details `message.json_response.details`
 */
export function resolveOptimizationSuggestions(details) {
  if (!details || typeof details !== 'object') return [];

  const analysis = details.analysis;
  const googleAds = asArray(analysis && typeof analysis === 'object' ? analysis.actionable_suggestions : null);
  if (googleAds.length) return googleAds;

  const actionable = asArray(details.actionable_items);
  if (actionable.length) return actionable;

  const dv360 = [
    ...asArray(details.pacing_recommendations).map(fromPacingRecommendation),
    ...asArray(details.recommendations).map(fromRecommendation),
  ];
  if (dv360.length) return dv360;

  // Nothing recognised. Return the arrays' own emptiness rather than a truthy
  // non-array, so the caller's `.length` check behaves.
  return [];
}

/**
 * The analysis prose above the cards. Google Ads nests it under `analysis`;
 * DV360 puts campaign_analysis at the top level of details and adds a pacing
 * summary and an overall optimization summary, none of which were being shown.
 */
export function resolveOptimizationAnalysisText(details) {
  if (!details || typeof details !== 'object') return null;
  const analysis = details.analysis;
  if (typeof analysis === 'string' && analysis.trim()) return analysis;

  const parts = [
    analysis?.campaign_analysis?.overall_performance,
    details.campaign_analysis?.overall_performance,
    details.campaign_analysis?.pacing_summary,
    details.optimization_summary,
    // The focused DV360 launchers (diagnostic / pacing / performance) each put
    // their summary under their own key at the top level of details.
    details.campaign_health?.summary,
    details.pacing_summary,
    details.performance_summary?.overall,
    // The Google Ads focused launchers put theirs one level down, under
    // `analysis`, and nothing read them. Measured on the 2026-08-30 IDFC
    // exports: 15 of 15 runs across all three agents rendered NO prose at all,
    // because `details` on a Google Ads result has exactly one key — `analysis`
    // — so every `details.<key>` lookup above is undefined.
    //
    // What was being discarded is not a stub. On one Saudi PMax run the pacing
    // agent wrote "reached its daily budget cap on 54 out of 60 measured days
    // (90%), indicating the budget, not demand, constrains delivery", and the
    // performance agent wrote "severely constrained by budget that was cut
    // mid-flight ... down from INR 1,800-2,400/day in early [window]" — a 73%
    // reduction that appears in no card and reached no reader.
    //
    // `analysis.summary` is deliberately NOT read: on the performance runs it
    // is a dict of counts (advisory / executable_now / total_recommendations),
    // not prose, and `performance_summary` carries the sentences.
    analysis?.pacing_summary,
    analysis?.pacing_verdict,
    analysis?.diagnostic_summary,
    analysis?.performance_summary?.headline,
    analysis?.performance_summary?.current_state,
  ].filter((p) => typeof p === 'string' && p.trim());

  // Dedupe: a result that repeats its summary in two keys should not print twice.
  const seen = new Set();
  const unique = parts.filter((p) => {
    const key = p.trim();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return unique.length ? unique.join('\n\n') : null;
}

export default {
  resolveOptimizationSuggestions,
  resolveOptimizationAnalysisText,
  blockingIssueRows,
};
